import { AxiosResponse } from "axios";

import {
  IAuditLog,
  IAuditLogFilterOptions,
  IAuditLogFilters,
  IEntityAuditLogFilters,
} from "../../@types/AuditLog";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * Table whose audit trail is NOT served by /Retool/AuditLogs: patient
 * conditions are reconstructed by a dedicated endpoint. Retool's
 * `moduleAuditLogs` swapped queries on exactly this value, and the shared
 * AuditLogPanel keeps that behaviour.
 */
export const AUDIT_LOG_CONDITION_TABLE = "cms_condition";

/**
 * Every audit read is paged server-side, at most 100 rows a page (EN-5288:
 * an unpaged read of a month of audit rows took staging down).
 */
export const AUDIT_LOG_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * The API stops counting one past this (a total above it means "more than")
 * and only pages through this many rows; past that, narrow the filters.
 */
export const AUDIT_LOG_MAX_ROWS = 10000;

/** Paging, window and filters shared by the two entity-scoped endpoints. */
function entityFilterParams(
  filters: Omit<IEntityAuditLogFilters, "tableName" | "entityId">
): URLSearchParams {
  const params = new URLSearchParams({
    Page: filters.page.toString(),
    PageSize: filters.pageSize.toString(),
  });

  if (filters.startDate) params.append("StartDate", filters.startDate);
  if (filters.endDate) params.append("EndDate", filters.endDate);
  filters.operations?.forEach((operation) =>
    params.append("Operations", operation)
  );
  if (filters.source) params.append("Source", filters.source);
  if (filters.applicationUserEmail) {
    params.append("ApplicationUserEmail", filters.applicationUserEmail);
  }

  return params;
}

export const auditLogService = {
  async getAuditLogs(
    filters: IAuditLogFilters
  ): Promise<AxiosResponse<IPaginatedResponse<IAuditLog[]>>> {
    const url = "/Retool/AuditLogs";
    const params = new URLSearchParams();

    params.append("Page", filters.page.toString());
    params.append("PageSize", filters.pageSize.toString());

    if (filters.schemaName) {
      params.append("SchemaName", filters.schemaName);
    }

    if (filters.tableName) {
      params.append("TableName", filters.tableName);
    }

    if (filters.operation) {
      params.append("Operation", filters.operation);
    }

    filters.operations?.forEach((operation) =>
      params.append("Operations", operation)
    );

    if (filters.source) {
      params.append("Source", filters.source);
    }

    if (filters.applicationUserId !== undefined) {
      params.append("ApplicationUserId", filters.applicationUserId.toString());
    }

    if (filters.applicationUserEmail) {
      params.append("ApplicationUserEmail", filters.applicationUserEmail);
    }

    if (filters.entityId) {
      params.append("EntityId", filters.entityId);
    }

    if (filters.startDate) {
      params.append("StartDate", filters.startDate);
    }

    if (filters.endDate) {
      params.append("EndDate", filters.endDate);
    }

    return api.get<IPaginatedResponse<IAuditLog[]>>(url, { params });
  },

  /** One row with its full before/after snapshots, which lists leave out. */
  async getAuditLog(id: string): Promise<AxiosResponse<IAuditLog>> {
    return api.get<IAuditLog>(`/Retool/AuditLogs/${id}`);
  },

  async getFilterOptions(): Promise<AxiosResponse<IAuditLogFilterOptions>> {
    return api.get<IAuditLogFilterOptions>("/Retool/AuditLogs/FilterOptions");
  },

  /** One page of the audit rows for one entity — Retool `moduleAuditLogs/getAuditLogs`. */
  async getEntityAuditLogs(
    filters: IEntityAuditLogFilters
  ): Promise<AxiosResponse<IPaginatedResponse<IAuditLog[]>>> {
    const { tableName, entityId, ...rest } = filters;
    const params = entityFilterParams(rest);
    params.append("TableName", tableName);
    params.append("EntityId", entityId);

    return api.get<IPaginatedResponse<IAuditLog[]>>("/Retool/AuditLogs", {
      params,
    });
  },

  /**
   * Patient condition changes — Retool
   * `moduleAuditLogs/getPatientConditionAuditLogs`. Conditions live in their
   * own audit shape, so this endpoint replaces the generic one whenever the
   * panel is pointed at `cms_condition`. Same window and filters as the
   * generic one.
   */
  async getPatientConditionAuditLogs(
    patientId: string,
    filters: Omit<IEntityAuditLogFilters, "tableName" | "entityId">
  ): Promise<AxiosResponse<IPaginatedResponse<IAuditLog[]>>> {
    return api.get<IPaginatedResponse<IAuditLog[]>>(
      `/Retool/Patients/${patientId}/GetConditionAuditLogs`,
      { params: entityFilterParams(filters) }
    );
  },
};
